import type { ApiV2Profile } from '@dce-front/hodor-types/api/v2/me/profiles/definitions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  DEFAULT_CACHE_TIME,
  DEFAULT_STALE_TIME,
} from '../../../constants/reactQuery';
import { useAppDispatch } from '../../../helpers/hooks/useAppDispatch';
import { useInvariantSelector } from '../../../helpers/hooks/useInvariantSelector';
import {
  removeAllProfiles,
  setCurrentProfile,
} from '../../../services/profiles/profileStorageService';
import {
  appKeySelector,
  getFeatureToggleProfile,
  hodorSdkConfigSelector,
  shouldDisplayWhoIsWatchingSelector,
} from '../../../store/slices/application-selectors';
import { userListProfile } from '../../../store/slices/user';
import {
  hasUserDataCollectedSelector,
  profilesUpdateDateSelector,
} from '../../../store/slices/user-selectors';
import { getProfiles, isKidsProfile } from './helpers/profilesHelper';

const USE_PROFILES_QUERY_KEY = 'useProfilesQuery';

/**
 *  Hook to manage profiles. It get profiles :
 * - from `localStorage` if data exist and `profilesUpdateDate` is up to date
 * - from hodor service if no data in `localStorage` or `profilesUpdateDate` is not up to date.
 *   And store result profiles to `localStorage` for the next time.
 * And store in redux the current profile
 *
 * Also, this hook expose 2 methods :
 * - `updateCurrentProfile` to select current profile and store it in localStorage, in cookie and in redux
 * - `refreshListProfiles` to force refresh profiles from hodor service (after an add or an update data profile)
 *
 * And expose properties :
 * - `profiles` list of all profiles
 * - `currentProfile` current profile selected
 * - `isKidsProfile` to indicate if the current profile is kids
 */
export const useProfiles = () => {
  const queryClient = useQueryClient();
  const appDispatch = useAppDispatch();
  const hodorSdkConfig = useSelector(hodorSdkConfigSelector);
  const profilesUpdateDate = useSelector(profilesUpdateDateSelector);
  const appKey = useInvariantSelector(appKeySelector);
  const featProfile = useInvariantSelector(getFeatureToggleProfile);
  const hasUserDataCollected = useInvariantSelector(
    hasUserDataCollectedSelector,
  );
  const shouldDisplayWhoIsWatching = useSelector(
    shouldDisplayWhoIsWatchingSelector,
  );

  /**
   * It get profiles :
   * - from `localStorage` if data exist and `profilesUpdateDate` is up to date
   * - from hodor service if no data in `localStorage` or `profilesUpdateDate` is not up to date.
   *   And store result profiles to `localStorage` for the next time.
   * And store in redux the current profile
   */
  const { data: profiles } = useQuery({
    queryKey: [USE_PROFILES_QUERY_KEY, profilesUpdateDate, appKey], // eslint-disable-line @tanstack/query/exhaustive-deps
    queryFn: async () => {
      if (!hodorSdkConfig) {
        return;
      }

      const dataProfiles = await getProfiles(
        hodorSdkConfig,
        profilesUpdateDate,
        appKey,
      );

      // update current profile in redux store
      if (dataProfiles && dataProfiles[0]) {
        appDispatch(userListProfile(dataProfiles[0]));
      }

      return dataProfiles;
    },
    gcTime: DEFAULT_CACHE_TIME,
    staleTime: DEFAULT_STALE_TIME,
    enabled:
      (featProfile || shouldDisplayWhoIsWatching) &&
      hasUserDataCollected &&
      Boolean(hodorSdkConfig),
  });

  /**
   * To select current profile and store it in localStorage and cookie
   * @param currentProfile
   */
  const updateCurrentProfile = useCallback(
    (currentProfile: ApiV2Profile) => {
      if (!profiles) {
        return;
      }

      // update storage local
      setCurrentProfile(currentProfile);

      return queryClient.invalidateQueries({
        queryKey: [USE_PROFILES_QUERY_KEY],
      });
    },
    [profiles, queryClient],
  );

  /**
   * To force refresh profiles from hodor service (after an add or an update data profile)
   */
  const refreshListProfiles = useCallback(async () => {
    // clear storage local to force get profiles from hodor
    removeAllProfiles();

    return queryClient.invalidateQueries({
      queryKey: [USE_PROFILES_QUERY_KEY],
    });
  }, [queryClient]);

  return {
    updateCurrentProfile,
    refreshListProfiles,
    isKidsProfile: profiles ? isKidsProfile(profiles[0]) : false,
    profiles: profiles ?? [],
    currentProfile: profiles ? profiles[0] : undefined,
  };
};
